import { Component } from '@angular/core';
import { Params } from '@angular/router';
import { defer, of, tap } from 'rxjs';

import { HeaderComponent } from './core/components/header/header.component';
import { ContentComponent } from './core/components/content/content.component';
import { PlaceHolderComponent } from './core/components/placeholder/placeholder.component';
import { AuthService } from './core/services/auth/auth.service';
import { Utils } from './core/classes/utils/utils.class';
import { STORAGE_HKEY, RANDOM_LENGTH, RANDOM_RADIX, RANDOM_START_INDEX, STORAGE_AUTH_TOKEN } from './core/constants/craig.constants';
import { version } from '../../package.json';
import { AuthResponse } from './interfaces/http.interface';
import { ErcDecodedTokenData } from './interfaces/access-token.interface';
import { UtilsService } from './core/services/utils/utils.service';
import { LocalStorageService } from './core/services/local-storage/local-storage.service';
import { ChatStorageService } from './core/services/chat-storage/chat-storage.service';
import { FeatureFlagService } from './modules/streaming-view/features/navigation/feature-flag.service';

@Component({
    selector: 'craig-root',
    standalone: true,
    imports: [HeaderComponent, ContentComponent, PlaceHolderComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.less'
})
export class AppComponent {
    isLoading: boolean = true;
    isStreamingEnabled: boolean = false;

    constructor(
        private readonly authService: AuthService,
        private readonly utilsService: UtilsService,
        private readonly localStorageService: LocalStorageService,
        private readonly _window: Window,
        private readonly chatStorageService: ChatStorageService,
        private readonly featureFlagService: FeatureFlagService
    ) {}

    ngOnInit(): void {
        // Store package version in localStorage first
        this.localStorageService.setRawItem('packageVersion', version);

        // Configure New Relic Browser Agent with application metadata
        // Only configure if not localhost (New Relic should not be loaded for localhost)
        const hostname: string = window.location.hostname;
        const isLocalhost: boolean = hostname === 'localhost' || hostname === '127.0.0.1';

        // eslint-disable-next-line no-console
        console.log('Version', version);

        if (!isLocalhost) {
            // Environment detection - determine environment before any New Relic calls
            const isProd: boolean = hostname === 'craig.parts.accuristech.com';
            const isDev: boolean = hostname === 'craig.dev.parts.accuristech.com';

            let environment: string = 'unknown';

            if (isProd) {
                environment = 'production';
                // eslint-disable-next-line no-underscore-dangle
                (this._window as any).__environment = 'production';

                // Styled console message with Accuris branding
                const styles: string = ['color: #1000E8', 'font-weight: bold'].join(';');
                const linkStyles: string = ['color: #4A90E2', 'font-weight: normal', 'text-decoration: underline'].join(
                    ';'
                );

                // eslint-disable-next-line no-console
                console.log('%c🚀 Accuris Technologies', styles);
                // eslint-disable-next-line no-console
                console.log('%cCheck out our careers page:', 'color: #666;');
                // eslint-disable-next-line no-console
                console.log('%chttps://jobs.dayforcehcm.com/en-US/accuris/CANDIDATEPORTAL', linkStyles);
            } else if (isDev) {
                environment = 'development';
                // eslint-disable-next-line no-underscore-dangle
                (this._window as any).__environment = 'development';
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/typedef
            const newrelic = (this._window as any).newrelic;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((this._window as any).NREUM && newrelic) {
                // eslint-disable-next-line no-console

                // Set custom attributes for the session using the global newrelic object
                if (newrelic.setCustomAttribute) {
                    // Application version tracking
                    newrelic.setCustomAttribute('appVersion', version);
                    newrelic.setCustomAttribute('packageVersion', version);

                    newrelic.setCustomAttribute('environment', environment);
                    newrelic.setCustomAttribute('hostname', hostname);

                    // eslint-disable-next-line no-console
                    console.log(`New Relic configured with version ${version} in ${environment} environment`);
                }

                // Set application version using first-class New Relic API
                if (newrelic.setApplicationVersion) {
                    newrelic.setApplicationVersion(version);
                }

                // Track page views with version info
                if (newrelic.addPageAction) {
                    newrelic.addPageAction('app_initialized', {
                        version,
                        environment,
                        timestamp: new Date().toISOString()
                    });

                    // Track app entry for funnel analysis
                    newrelic.addPageAction('app_entry', {
                        version,
                        environment,
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent,
                        screenResolution: `${screen.width}x${screen.height}`,
                        viewportSize: `${this._window.innerWidth}x${this._window.innerHeight}`,
                        sessionId: this.generateSessionId()
                    });
                }
            } else if ((this._window as any).NREUM) {
                // console.log('New Relic Browser Agent is loading');
            } else {
                // eslint-disable-next-line no-console
                console.warn('New Relic Browser Agent not detected - this is expected for localhost');
            }
        }

        const { ercToken, hkey }: Params = Utils.getParamsFromUrl(this._window.location.href);

        defer(() => {
            if (ercToken !== undefined || this.localStorageService.getRawItem(STORAGE_AUTH_TOKEN) === null) {
                this.utilsService.cleanAuthDataStorage();

                return this.authService.login(ercToken || '').pipe(
                    tap({
                        next: (authResponse: AuthResponse | null) => {
                            if (authResponse !== null) {
                                const { token, token_type: tokenType }: AuthResponse = authResponse;

                                if (token && tokenType) {
                                    const bearerToken: string = `${tokenType} ${token}`;

                                    try {
                                        this.saveAuthTokenToLocalStorage(bearerToken);
                                    } catch (error) {
                                        if (this.localStorageService.isStorageExceededError(error)) {
                                            this.utilsService.cleanChatSessionStorage();
                                            this.chatStorageService.changeSessionsList();

                                            this.saveAuthTokenToLocalStorage(bearerToken);
                                        }
                                    }
                                }
                            }
                        }
                    })
                );
            }

            return of(undefined);
        }).subscribe(() => {
            const authToken: string | null = this.localStorageService.getRawItem(STORAGE_AUTH_TOKEN);

            if (!!authToken) {
                const decodedToken: ErcDecodedTokenData | null = this.decodeAuthToken(authToken);

                if (decodedToken) {
                    this.authService.setUserEmail(decodedToken.email);

                    if (hkey) {
                        try {
                            this.saveHKeyToLocalStorage(hkey);
                        } catch (error) {
                            if (this.localStorageService.isStorageExceededError(error)) {
                                this.utilsService.cleanChatSessionStorage();
                                this.chatStorageService.changeSessionsList();
                                this.saveHKeyToLocalStorage(hkey);
                            }
                        }
                    }

                    // Load feature flags after successful authentication
                    this.featureFlagService.loadFeatureFlags().subscribe({
                        next: () => {
                            // Use getFeatureFlag to respect local overrides
                            this.isStreamingEnabled = this.featureFlagService.getFeatureFlag('streamingChat');
                            this.isLoading = false;
                        },
                        error: (error: any) => {
                            // eslint-disable-next-line no-console
                            console.error('❌ Failed to load feature flags:', error);
                            this.isStreamingEnabled = false; // Default to legacy
                            this.isLoading = false; // Continue even if feature flags fail
                        }
                    });
                    // Track user authentication in New Relic (only for non-localhost)
                    if (!isLocalhost) {
                        const newrelic: any = (this._window as any).newrelic;

                        if (newrelic) {
                            // Set user ID for session tracking using first-class New Relic API
                            if (newrelic.setUserId) {
                                newrelic.setUserId(decodedToken.email);
                            }

                            // Enhanced user attributes for better tracking
                            if (newrelic.setCustomAttribute) {
                                newrelic.setCustomAttribute('userEmail', decodedToken.email);
                                newrelic.setCustomAttribute('username', decodedToken.username);
                                newrelic.setCustomAttribute('userProduct', decodedToken.product);
                                newrelic.setCustomAttribute('userSessionId', decodedToken.session_id);
                            }

                            // Track authentication event with version info
                            if (newrelic.addPageAction) {
                                newrelic.addPageAction('user_authenticated', {
                                    userEmail: decodedToken.email,
                                    appVersion: version,
                                    timestamp: new Date().toISOString()
                                });

                                // Track streaming view entry for funnel analysis
                                if (this.isStreamingEnabled) {
                                    newrelic.addPageAction('streaming_view_entry', {
                                        userEmail: decodedToken.email,
                                        appVersion: version,
                                        timestamp: new Date().toISOString(),
                                        featureFlag: 'streamingChat'
                                    });
                                }
                            }
                        }
                    }

                    this.reloadPageWithoutLoginParams();
                }
            }
        });
    }

    private reloadPageWithoutLoginParams(): void {
        const { pathname }: Location = this._window.location;

        this._window.history.replaceState({}, '', pathname);
    }

    private decodeAuthToken(token: string): ErcDecodedTokenData {
        const jwtToken: string = token.split(' ')[1];

        return Utils.decodeToken(jwtToken);
    }

    private saveAuthTokenToLocalStorage(token: string): void {
        this.localStorageService.setRawItem(STORAGE_AUTH_TOKEN, token);
    }

    private saveHKeyToLocalStorage(hKey: string): void {
        this.localStorageService.setRawItem(STORAGE_HKEY, hKey);
    }

    /**
     * Generate a session ID for tracking user sessions
     */
    private generateSessionId(): string {
        // Try to get existing session ID from localStorage, or create a new one
        const existingSessionId: string | null = localStorage.getItem('craig_session_id');

        if (existingSessionId) {
            return existingSessionId;
        }

        const randomPart: string = Math.random()
            .toString(RANDOM_RADIX)
            .slice(RANDOM_START_INDEX, RANDOM_START_INDEX + RANDOM_LENGTH);

        const sessionId: string = `session_${Date.now()}_${randomPart}`;

        localStorage.setItem('craig_session_id', sessionId);

        return sessionId;
    }
}
